import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CONTACT, NAV_LINKS } from "../data";
import { MapPin, Phone, Mail, Instagram, Facebook, ArrowUpRight, Send } from "lucide-react";
import { saveSubscriber } from "../firebase";

export default function Footer() {
  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="bg-ink text-bone relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.82' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
        aria-hidden
      />

      {/* Big newsletter / wordmark block */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-28">
        <p className="section-num text-bone/40">
          <strong className="text-butter">·</strong> Keep in the loop
        </p>
        <div className="mt-5 grid md:grid-cols-12 gap-10 items-end">
          <h3 className="md:col-span-7 font-display text-display-lg text-bone">
            Fresh notes from the
            <br />
            <span className="font-italic text-butter">growing room.</span>
          </h3>
          <form
            data-testid="footer-subscribe-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const data = new FormData(form);
              const email = (data.get("email") || "").toString().trim();
              if (!email) return;
              try {
                await saveSubscriber(email, "footer");
                form.reset();
                toast.success("Subscribed", {
                  description: `We'll write to ${email} when we have something good.`,
                });
              } catch (err) {
                console.error(err);
                toast.error("Couldn't subscribe right now", {
                  description: "Please try again in a moment.",
                });
              }
            }}
            className="md:col-span-5 flex items-center gap-2 rounded-full bg-bone/10 border border-bone/15 p-1.5 pl-5"
          >
            <input
              data-testid="footer-subscribe-input"
              type="email"
              name="email"
              required
              placeholder="your@email.com"
              className="flex-1 bg-transparent text-bone placeholder:text-bone/40 text-sm outline-none py-2"
            />
            <button
              data-testid="footer-subscribe-submit"
              type="submit"
              className="h-10 px-5 rounded-full bg-butter text-ink text-sm font-medium inline-flex items-center gap-2 hover:bg-coral hover:text-white transition-colors"
            >
              Subscribe <Send className="h-3.5 w-3.5" />
            </button>
          </form>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Link to="/" className="flex items-center gap-3">
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-boneSoft p-2">
              <img
                src={CONTACT.logo}
                alt="Organo Mushroom"
                className="h-full w-full object-contain"
              />
            </span>
            <span className="font-display text-2xl">
              Organo <span className="font-italic text-butter">Mushroom</span>
            </span>
          </Link>
          <p className="mt-6 text-bone/70 max-w-md leading-relaxed text-[15px]">
            A small family farm in Nagpur growing premium oyster mushrooms with
            care. FSSAI certified, hand-harvested, delivered fresh.
          </p>
          <div className="mt-8 flex gap-3">
            <a
              data-testid="social-instagram"
              href="#"
              className="group h-11 w-11 grid place-items-center rounded-full border border-bone/15 hover:bg-coral hover:border-coral transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="h-4 w-4 group-hover:scale-110 transition-transform" />
            </a>
            <a
              data-testid="social-facebook"
              href="#"
              className="group h-11 w-11 grid place-items-center rounded-full border border-bone/15 hover:bg-coral hover:border-coral transition-colors"
              aria-label="Facebook"
            >
              <Facebook className="h-4 w-4 group-hover:scale-110 transition-transform" />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="text-[10px] uppercase tracking-[0.28em] text-bone/50">
            Sitemap
          </p>
          <ul className="mt-5 space-y-3 text-bone/85">
            {NAV_LINKS.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  className="link-underline text-[14.5px] hover:text-butter transition-colors"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-[10px] uppercase tracking-[0.28em] text-bone/50">
            Get in touch
          </p>
          <ul className="mt-5 space-y-3.5">
            <li className="text-bone/85 flex gap-3 text-[14.5px]">
              <MapPin className="h-4 w-4 text-butter mt-1 flex-shrink-0" />
              <span data-testid="footer-address">{CONTACT.address}</span>
            </li>
            {CONTACT.phones.map((p) => (
              <li key={p}>
                <a
                  data-testid={`footer-phone-${p.replace(/\s/g, "")}`}
                  href={`tel:+91${p.replace(/\s/g, "")}`}
                  className="link-underline inline-flex items-center gap-3 text-bone/85 hover:text-butter transition-colors text-[14.5px]"
                >
                  <Phone className="h-4 w-4" /> +91 {p}
                </a>
              </li>
            ))}
            <li>
              <a
                data-testid="footer-email"
                href={`mailto:${CONTACT.email}`}
                className="link-underline inline-flex items-center gap-3 text-bone/85 hover:text-butter transition-colors text-[14.5px]"
              >
                <Mail className="h-4 w-4" /> {CONTACT.email}
              </a>
            </li>
            <li>
              <a
                data-testid="footer-website"
                href={CONTACT.website}
                target="_blank"
                rel="noreferrer"
                className="link-underline inline-flex items-center gap-3 text-bone/85 hover:text-butter transition-colors text-[14.5px]"
              >
                organomushroom.com
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Wordmark band */}
      <div className="relative border-t border-bone/10 overflow-hidden">
        <p className="font-display text-[clamp(4rem,14vw,13rem)] leading-none tracking-tightest text-bone/[0.06] text-center whitespace-nowrap py-8 select-none">
          ORGANO <span className="font-italic">mushroom</span>
        </p>
      </div>

      <div className="relative border-t border-bone/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center justify-between gap-4 text-xs text-bone/50">
          <p>© {new Date().getFullYear()} Organo Mushroom · Nagpur, India</p>
          <p className="font-italic text-sm text-bone/65">
            FSSAI Reg. 21250275000103 — Crafted with care.
          </p>
        </div>
      </div>
    </footer>
  );
}
